<template>
  <v-container class="pa-0" height="100%">
    <CardTemplate id="update_userid_card" @show-modal="showDialog">
      <template #icon v-if="hasTouchId">
        <v-img
          class="account-img"
          style="
            filter: invert(43%) sepia(17%) saturate(1779%) hue-rotate(165deg) brightness(92%)
              contrast(94%);
          "
          src="@/assets/TouchID.svg"
          alt="Touch Id"
          width="46px"
          height="46px"
        />
      </template>
      <template #icon v-else-if="hasFaceId">
        <v-img
          class="account-img"
          style="
            filter: invert(43%) sepia(17%) saturate(1779%) hue-rotate(165deg) brightness(92%)
              contrast(94%);
          "
          src="@/assets/FaceID.svg"
          alt="face id"
          width="46px"
          height="46px"
        />
      </template>
      <template #title>
        {{ hasTouchId ? $t('L_manage_fingerprint_id') : $t('L_manage_face_id') }}
      </template>
      <template #content>
        <p :class="!$vuetify.display.xs ? 'd-flex align-center justify-center mt-4' : 'd-none'">
          &nbsp;
        </p>
      </template>
      <template #action>
        <OnexButton
          @click="showDialog"
          variant="text"
          id="update_userid_trigger"
          :aria-label="$t('L_update_biometrics_preference')"
        >
          {{ $t('L_edit') }}
        </OnexButton>
      </template>
    </CardTemplate>
    <MyAccountDialog
      :header="hasTouchId ? $t('L_manage_fingerprint_id') : $t('L_manage_face_id')"
      :show="showUpdateBiometricsPreferenceDialog"
      :show-save-button="false"
      @close="closeDialog"
      :close-is-active="closeIsActive"
      save-changes-btn-id="save_biometrics_preference_id"
      close-btn-id="close_biometrics_preference_id"
      close-btn-class="update_biometrics_preference_close"
      modal-class="update_biometrics_preference_modal"
      submit-btn-text="L_next"
    >
      <v-row>
        <LoadingIndicator :loading="updateInProgress" />
        <p class="body-1">
          {{ hasTouchId ? $t('L_manage_fingerprint_id_prompt') : $t('L_manage_face_id_prompt') }}
        </p>
        <v-row class="mt-3 ml-4 mr-4">
          <v-col cols="7" class="mt-2">
            <v-img
              v-if="hasTouchId"
              height="40"
              width="40"
              src="@/assets/TouchID.svg"
              class="mr-2"
              alt="Touch Id"
            />
            <v-img
              v-else-if="hasFaceId"
              height="40"
              width="40"
              src="@/assets/FaceID.svg"
              class="mr-2"
              alt="face id"
            />
          </v-col>
          <v-col cols="4">
            <v-switch
              v-model="biometricsPreference"
              inset
              color="primary"
              :label="biometricsPreferenceSwitchLabel"
              @update:modelValue="changeTheBiometricsPreference"
              class="mt-0 pb-0 sizeOfBiometricsToggle"
              style="width: 156px !important; justify-content: flex-end"
            />
          </v-col>
        </v-row>
        <v-row class="pb-5 mb-5">
          <v-col cols="12">
            <span v-if="hasTouchId" class="font-weight-bold font-size--14">
              {{ $t('L_touch_id_sharing_your_device_with_anyone_who') }}
            </span>
            <span v-else-if="hasFaceId" class="font-weight-bold font-size--14">
              {{ $t('L_face_id_sharing_your_device_with_anyone_who') }}
            </span>
          </v-col>
        </v-row>
      </v-row>
    </MyAccountDialog>
    <MessageDialog
      :show-close-button="false"
      :show-dialog="isConfirmationDialogOpen"
      :has-button-two="false"
      :is-persistent="true"
      type="success"
      :title="confirmationMessage"
      :message="
        biometricsPreference
          ? $t('L_you_can_now_login_without_using_u_and_p')
          : $t('L_you_can_now_login_with_using_u_and_p')
      "
      :button-one-label="$t('L_OK')"
      @buttonOneAction="closeConfirmationDialog"
    />
  </v-container>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import {
  hasFaceId,
  hasTouchId,
  setUnlockModeToBiometrics,
  setUnlockModeToSecureStorage,
  isUnlockModePreferenceSetToBiometrics,
} from '@/plugins/auth/utils/sessionManager';
import { mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { OnexButton, OnexDialog } from '@primerica/onex-ui-lib';
import MyAccountDialog from './MyAccountDialog.vue';
import CardTemplate from './CardTemplate.vue';
import { mdiCheckCircleOutline, mdiClose } from '@mdi/js';
import MessageDialog from '@/components/MessageDialog.vue';

const componentOptions: ComponentOptions = {
  name: 'UpdateBiometricsPreference',
  components: {
    CardTemplate,
    MyAccountDialog,
    LoadingIndicator,
    OnexButton,
    OnexDialog,
    MessageDialog,
  },
  data: () => ({
    showUpdateBiometricsPreferenceDialog: false,
    isConfirmationDialogOpen: false,
    closeIsActive: false,
    biometricsPreference: false,
    updateInProgress: false,
    hasFaceId: false,
    hasTouchId: false,
    mdiCheckCircleOutline,
    mdiClose,
  }),
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    confirmationMessage() {
      let confirmationMessage;
      if (this.biometricsPreference) {
        confirmationMessage = this.hasTouchId
          ? this.$t('L_fingerprint_id_enabled')
          : this.$t('L_face_id_enabled');
      } else {
        confirmationMessage = this.hasTouchId
          ? this.$t('L_fingerprint_id_disabled')
          : this.$t('L_face_id_disabled');
      }
      return confirmationMessage;
    },
    biometricsPreferenceSwitchLabel() {
      return this.biometricsPreference
        ? this.$t('L_biometrics_preference_on')
        : this.$t('L_biometrics_preference_off');
    },
  },
  methods: {
    showDialog() {
      this.showUpdateBiometricsPreferenceDialog = true;
    },
    closeDialog() {
      this.showUpdateBiometricsPreferenceDialog = false;
    },
    async changeTheBiometricsPreference() {
      if (this.updateInProgress) return;
      this.updateInProgress = true;
      this.isConfirmationDialogOpen = true;
      if (this.biometricsPreference) {
        // Set Unlock Mode To Biometrics
        await setUnlockModeToBiometrics().catch(async (e) => {
          try {
            this.$dynatrace.reportCustomError(
              'PC2_MOBILE_ERR',
              `[UpdateBiometricsPreference] ${e?.code}: ${e?.message || JSON.stringify(e)}`
            );
            await this.globalStore.postAuditLog({
              actionId: 'MobileApp_Device_FailureAction',
              details: `[${Date.now()}] ERROR CODE ${e?.code}`,
              message: e?.message,
            });
          } catch (_error) {
            /* empty */
          }
        });
      } else {
        // Set Unlock Mode To Secure Storage
        await setUnlockModeToSecureStorage().catch(async (e) => {
          try {
            this.$dynatrace.reportCustomError(
              'PC2_MOBILE_ERR',
              `[UpdateBiometricsPreference] ${e?.code}: ${e?.message || JSON.stringify(e)}`
            );
            await this.globalStore.postAuditLog({
              actionId: 'MobileApp_Device_FailureAction',
              details: `[${Date.now()}] ERROR CODE ${e?.code}`,
              message: e?.message,
            });
          } catch (_error) {
            /* empty */
          }
        });
      }
      this.updateInProgress = false;
    },
    closeConfirmationDialog() {
      this.isConfirmationDialogOpen = false;
      this.showUpdateBiometricsPreferenceDialog = false;
    },
  },
  async created() {
    await Promise.all([hasFaceId(), hasTouchId(), isUnlockModePreferenceSetToBiometrics()]).then(
      ([_hasFaceId, _hasTouchId, _isUnlockModePreferenceSetToBiometrics]) => {
        this.hasFaceId = _hasFaceId;
        // eslint-disable-next-line no-console
        console.log('🚀 ~ this.hasFaceId:', this.hasFaceId);
        this.hasTouchId = _hasTouchId;
        // eslint-disable-next-line no-console
        console.log('🚀 ~ this.hasTouchId:', this.hasTouchId);
        this.biometricsPreference = _isUnlockModePreferenceSetToBiometrics;
      }
    );
    // TODO: handle error or log in DT
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.sizeOfBiometricsToggle {
  font-size: 12px;
}
</style>
